import React, { useState, useEffect } from 'react';
import { api } from '../../api/client';
import { PlanItem } from '../../types';
import { Modal } from '../../components/common/Modal';
import { useAuth } from '../../context/AuthContext';
import { Layers, Edit3, Check, Sparkles } from 'lucide-react';

export const PlansPage: React.FC = () => {
  const { can } = useAuth();
  const [plans, setPlans] = useState<PlanItem[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<PlanItem | null>(null);

  // Edit form state
  const [editPriceMo, setEditPriceMo] = useState<number>(0);
  const [editPriceYr, setEditPriceYr] = useState<number>(0);
  const [editCamLimit, setEditCamLimit] = useState<number>(0);
  const [editRetention, setEditRetention] = useState<number>(0);
  // Iris (ask-your-footage) monthly credit allowance: -1 = unlimited, 0 = off.
  const [editAiCredits, setEditAiCredits] = useState<number>(0);
  const [editFaces, setEditFaces] = useState<boolean>(false); // ai_face_recognition
  const [editActive, setEditActive] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const data = await api.getPlans();
      // Stable order: active first, then by monthly price.
      setPlans([...data].sort((a, b) => Number(b.isActive) - Number(a.isActive) || a.priceMonthlyUsd - b.priceMonthlyUsd));
    } catch (err) {
      console.error('Failed to load plans', err);
    }
  };

  const openEditModal = (plan: PlanItem) => {
    setSelectedPlan(plan);
    setEditPriceMo(plan.priceMonthlyUsd);
    setEditPriceYr(plan.priceYearlyUsd);
    setEditCamLimit(plan.cloudCameraLimit);
    setEditRetention(plan.retentionDays);
    const credRaw = (plan.rawFeatures ?? []).find((f) => f.key === 'ai_credits_monthly')?.value;
    setEditAiCredits(credRaw != null && credRaw !== '' ? parseInt(credRaw, 10) || 0 : 0);
    const facesRaw = (plan.rawFeatures ?? []).find((f) => f.key === 'ai_face_recognition')?.value;
    setEditFaces(facesRaw != null && facesRaw !== '' && facesRaw !== 'false' && facesRaw !== '0');
    setEditActive(plan.isActive);
    setError(null);
  };

  const handleSavePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPlan) return;
    setSaving(true);
    setError(null);
    try {
      // Rebuild the FULL feature set (PUT replaces it wholesale), overriding the
      // two numeric quotas the form edits and preserving everything else.
      const feats = new Map((selectedPlan.rawFeatures ?? []).map((f) => [f.key, f.value]));
      feats.set('cloud_cameras', String(editCamLimit));
      feats.set('cloud_storage_days', String(editRetention));
      // Iris is available on every plan; the allowance is what differs. Keep the
      // assistant flag on and write the monthly credit allowance.
      feats.set('ai_assistant', 'true');
      feats.set('ai_credits_monthly', String(editAiCredits));
      feats.set('ai_face_recognition', editFaces ? 'true' : 'false');
      const features = Array.from(feats, ([key, value]) => ({ key, value }));

      await api.updatePlan(selectedPlan.slug, {
        priceMonthly: editPriceMo,
        priceYearly: editPriceYr,
        isActive: editActive,
        features,
      });
      setSelectedPlan(null);
      loadPlans();
    } catch (err: any) {
      setError(err?.message || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div
        style={{
          background: 'var(--bg-surface-card)',
          border: '1px solid var(--border-dim)',
          borderRadius: 'var(--radius-lg)',
          padding: '16px 20px',
          marginBottom: '24px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
          <Layers size={18} color="var(--primary-light)" />
          <h2 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-main)' }}>
            Service Plan Catalogue
          </h2>
        </div>
        <p style={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>
          Changes affect newly enrolled or renewed customer entitlements and quotas.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
        {plans.map((plan) => (
          <div key={plan.slug} className="card" style={{ display: 'flex', flexDirection: 'column', opacity: plan.isActive ? 1 : 0.6 }}>
            <div className="card-header">
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-main)' }}>
                    {plan.name}
                  </h3>
                  {!plan.isActive && (
                    <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-dim)', border: '1px solid var(--border-dim)', borderRadius: 999, padding: '1px 7px' }}>Retired</span>
                  )}
                </div>
                <code style={{ fontSize: '11px', color: 'var(--primary-light)' }}>{plan.slug}</code>
              </div>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => openEditModal(plan)}
                disabled={!can('billing:edit_plan')}
                title="Edit plan pricing and limits (audited)"
              >
                <Edit3 size={13} />
                <span>Edit</span>
              </button>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '28px', fontWeight: 800, color: 'var(--text-main)' }}>
                ${plan.priceMonthlyUsd}
                <span style={{ fontSize: '13px', fontWeight: 400, color: 'var(--text-muted)' }}> / mo</span>
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-dim)' }}>
                or ${plan.priceYearlyUsd} / yr (annual discount)
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '12.5px', color: 'var(--text-muted)', flex: 1, marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Check size={14} color="var(--success-text)" />
                <span>Max Cloud Cameras: <strong style={{ color: 'var(--text-main)' }}>{plan.cloudCameraLimit}</strong></span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Check size={14} color="var(--success-text)" />
                <span>Video Cloud Retention: <strong style={{ color: 'var(--text-main)' }}>{plan.retentionDays} Days</strong></span>
              </div>
              <div style={{ marginTop: '8px' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '4px' }}>
                  AI Analytics Capabilities:
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {plan.aiFeatures.map((feat) => (
                    <span
                      key={feat}
                      style={{
                        background: 'rgba(139, 92, 246, 0.1)',
                        border: '1px solid rgba(139, 92, 246, 0.25)',
                        color: 'var(--purple-text)',
                        borderRadius: '4px',
                        padding: '2px 6px',
                        fontSize: '11px',
                      }}
                    >
                      {feat.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Plan Modal */}
      <Modal
        isOpen={!!selectedPlan}
        onClose={() => setSelectedPlan(null)}
        title={`Edit Plan: ${selectedPlan?.name}`}
        subtitle="Modifies quotas and pricing parameters (Audited)"
      >
        <form onSubmit={handleSavePlan}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                Price Monthly ($ USD)
              </label>
              <input
                type="number"
                className="input"
                style={{ width: '100%' }}
                value={editPriceMo}
                onChange={(e) => setEditPriceMo(parseFloat(e.target.value) || 0)}
                required
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                Price Yearly ($ USD)
              </label>
              <input
                type="number"
                className="input"
                style={{ width: '100%' }}
                value={editPriceYr}
                onChange={(e) => setEditPriceYr(parseFloat(e.target.value) || 0)}
                required
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                Cloud Camera Cap
              </label>
              <input
                type="number"
                className="input"
                style={{ width: '100%' }}
                value={editCamLimit}
                onChange={(e) => setEditCamLimit(parseInt(e.target.value) || 1)}
                required
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                Retention (Days)
              </label>
              <input
                type="number"
                className="input"
                style={{ width: '100%' }}
                value={editRetention}
                onChange={(e) => setEditRetention(parseInt(e.target.value) || 1)}
                required
              />
            </div>
          </div>

          <div style={{ marginBottom: '14px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>
              Iris credits / month (1 credit = 1 question · −1 = unlimited · 0 = off)
            </label>
            <input
              type="number"
              className="input"
              style={{ width: '100%' }}
              value={editAiCredits}
              onChange={(e) => setEditAiCredits(parseInt(e.target.value, 10) || 0)}
              required
            />
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={editFaces} onChange={(e) => setEditFaces(e.target.checked)} />
            <span>Cloud face recognition (known faces). Runs for owners who have enrolled faces; strangers raise an alert.</span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 16, cursor: 'pointer' }}>
            <input type="checkbox" checked={editActive} onChange={(e) => setEditActive(e.target.checked)} />
            <span>Plan is active (sold to new/renewing customers). Unchecked = retired — existing subscribers keep it, no one new lands on it.</span>
          </label>

          {error && <div style={{ color: 'var(--danger-text)', fontSize: 12, marginBottom: 12 }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setSelectedPlan(null)}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
              <Check size={14} />
              <span>{saving ? 'Saving…' : 'Save plan'}</span>
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
